import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../../utils/toast';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

import { getCurrentEvents } from '../../services/api';
import { EventListResponse } from '../../types/types';
import {
  formatDate,
  getEventAvatarColor,
  getEventInitials,
} from '../../utils/common/formatUtils';
import { EmptyState } from './emptyState';
import { Skeleton } from './skeleton';

interface EventImageProps {
  src?: string;
  fallbackText: string;
  fallbackBg: string;
  fallbackColor: string;
}

// Image fills the whole media area of the card (cover), with a shimmer until it loads
// and an initials fallback if there's no image or it fails to load.
const EventImage: React.FC<EventImageProps> = ({ src, fallbackText, fallbackBg, fallbackColor }) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [hasFailed, setHasFailed] = useState<boolean>(false);
  const showImage = !!src && !hasFailed;

  return (
    <div
      className="relative flex h-40 w-full shrink-0 items-center justify-center overflow-hidden rounded-2xl text-4xl font-extrabold"
      style={showImage ? undefined : { backgroundColor: fallbackBg, color: fallbackColor }}
    >
      {showImage ? (
        <>
          {!isLoaded && <Skeleton className="absolute inset-0 rounded-none" />}
          <img
            src={src}
            alt=""
            loading="lazy"
            onLoad={() => setIsLoaded(true)}
            onError={() => setHasFailed(true)}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </>
      ) : (
        fallbackText
      )}
    </div>
  );
};

const EventCardSkeleton: React.FC = () => (
  <div className="flex w-64 shrink-0 flex-col gap-4 rounded-3xl border border-slate-200 p-4">
    <Skeleton className="h-40 w-full rounded-2xl" />
    <Skeleton className="h-5 w-3/4" />
    <div className="flex flex-col gap-2">
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  </div>
);

export const CurrentEventsCarousel: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const [events, setEvents] = useState<EventListResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let isCancelled = false;

    getCurrentEvents()
      .then((data) => {
        if (!isCancelled) setEvents(data || []);
      })
      .catch(() => {
        if (!isCancelled) toast.error(t('failed_to_load_current_events'));
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [t]);

  const scrollTrack = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (track) track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
  };

  const isEmpty = !isLoading && events.length === 0;

  return (
    <div className="flex h-full min-w-0 flex-col gap-4 rounded-3xl bg-white p-4 shadow-sm ring-1 ring-black/5 font-['Plus_Jakarta_Sans',sans-serif]">
      <p className="text-sm font-medium leading-5 text-gray-900">{t('current_events')}</p>

      {isLoading ? (
        <div role="status" aria-busy="true" className="flex gap-4 overflow-hidden">
          {Array.from({ length: 4 }).map((_, index) => (
            <EventCardSkeleton key={index} />
          ))}
        </div>
      ) : isEmpty ? (
        <EmptyState text={t('no_data_to_display')} />
      ) : (
        <>
          <div ref={trackRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth">
            {events.map((event) => {
              const avatarColor = getEventAvatarColor(event.name || '');
              const logo = event.organizer?.profileImage;
              const place = [event.venue, event.country].filter(Boolean).join(', ');

              return (
                <div
                  key={event.id}
                  className="flex w-64 shrink-0 snap-start flex-col gap-4 rounded-3xl border border-slate-200 p-4"
                >
                  <EventImage
                    src={logo}
                    fallbackText={getEventInitials(event.name)}
                    fallbackBg={avatarColor.bg}
                    fallbackColor={avatarColor.text}
                  />
                  <p className="truncate text-base font-semibold text-gray-900">{event.name}</p>
                  <div className="flex flex-col gap-2 text-sm text-gray-700">
                    <span className="flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4 shrink-0" />
                      {formatDate(event.startDate)}
                    </span>
                    {place && (
                      <span className="flex items-center gap-2">
                        <MapPinIcon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{place}</span>
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {events.length > 0 && (
            <div className="flex items-center justify-center gap-4">
              <button
                type="button"
                aria-label={t('previous_slide')}
                onClick={() => scrollTrack(-1)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label={t('next_slide')}
                onClick={() => scrollTrack(1)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
